// Chargers translations for React Admin resource
export const chargersResource = {
  name: 'Chargers',
  fields: {
    id: 'ID',
    name: 'Name',
    siteId: 'Premises',
    loadGroupId: 'Load Group',
    model: 'Model',
    serialNumber: 'Serial Number',
    ocppVersion: 'OCPP Version',
    status: 'Status',
    power: 'Power Display',
    maxPower: 'Max Power (kW)',
    maxCurrent: 'Max Current (A)',
    numberPhases: 'Number of Phases',
    numberPhasesOne: '1 (Single-phase)',
    numberPhasesThree: '3 (Three-phase)',
    currentPower: 'Current Power (kW)',
    connectorType: 'Connector Type',
    connectorCount: 'Connector Count',
    connectors: 'Connectors',
    maxSimultaneousConnectors: 'Max Simultaneous Charging',
    connectorId: 'Connector ID',
    chargerType: 'Charger Type',
    firmwareVersion: 'Firmware Version',
    ocppStatus: 'OCPP Status',
    errorCode: 'Error Code',
    physicalLocation: 'Physical Location',
    bayNumber: 'Bay Number',
    zone: 'Zone',
    qrCode: 'Manual Entry Code',
    notes: 'Notes',
    lastStatusUpdate: 'Last Status Update',
    createdAt: 'Created At',
    updatedAt: 'Updated At',
    site: 'Premises',
    stationId: 'Station ID',
    authUsername: 'Auth Username',
    authPassword: 'Auth Password',
    authEnabled: 'Authentication Enabled',
    ocppTemplateId: 'OCPP Template',
    noTemplate: 'No template',
    image: 'Charger Image',
    smartChargingConfig: {
      title: 'Smart Charging Capabilities',
      description: 'OCPP GetConfiguration values reported by this charger. Used by the LMS to decide whether to send normal, merged-slot, or no profile.',
      maxStackLevel: 'Max Stack Level',
      supportedFeatureProfiles: 'Supported Feature Profiles',
      chargingRateUnits: 'Allowed Charging Rate Units',
      maxPeriods: 'Max Schedule Periods',
      maxProfilesInstalled: 'Max Profiles Installed',
      phaseSwitchSupported: '3-to-1 Phase Switch Supported',
      firmwareVersionAtQuery: 'Firmware Version at Query',
      queriedAt: 'Last Queried At',
      notYetQueried: 'Not yet queried. The OCPP server will query on next charger connect, or click Refresh to query now.',
      refreshButton: 'Refresh',
      refreshing: 'Refreshing…',
      refreshSuccess: 'Smart charging capabilities refreshed',
      refreshFailed: 'Failed to refresh smart charging capabilities',
    },
  },
};

// Custom chargers translations for components
export const chargers = {
  // Charger-level statuses. A charger can have many connectors, so this is
  // a rollup — "Online" means "reachable", not the OCPP connector state.
  statuses: {
    available: 'Online',
    charging: 'Charging',
    error: 'Error',
    maintenance: 'Maintenance',
    offline: 'Offline',
    unavailable: 'Unavailable',
    faulted: 'Faulted',
    suspended: 'Suspended',
    suspended_ev: 'Suspended EV',
    suspended_evse: 'Suspended EVSE',
    finishing: 'Finishing',
    reserved: 'Reserved',
    preparing: 'Preparing',
    in_use: 'In Use',
  },
  // Per-connector OCPP 1.6 status names (ConnectorStatusEnumType). These
  // must follow the OCPP standard exactly — do NOT map 'available' to
  // "Online" like the charger-level statuses above do.
  // Keys are normalized to lower_snake_case by getConnectorStatusKey() in
  // ChargersShow.tsx.
  connectorStatuses: {
    available: 'Available',
    preparing: 'Preparing',
    charging: 'Charging',
    suspended_ev: 'Suspended EV',
    suspended_evse: 'Suspended EVSE',
    finishing: 'Finishing',
    reserved: 'Reserved',
    unavailable: 'Unavailable',
    faulted: 'Faulted',
    // Non-OCPP statuses that may appear because of local policy overrides:
    offline: 'Offline',
    occupied: 'Occupied',
  },
  list: {
    title: 'Chargers',
    filters: {
      search: 'Search by name, model, or serial number',
      site: 'Premises',
      status: 'Status',
      connectorType: 'Connector Type',
      ocppVersion: 'OCPP Version',
    },
  },
  create: {
    title: 'Create Charger',
    subtitle: 'Add a new EV charger to the network',
    infoMessage: 'Create a new EV charger. Ensure you have selected the correct site and load group before proceeding.',
    basicInfo: 'Basic Information',
    image: 'Charger Image',
    imageHelperText: 'Upload an image of the charger for display in the mobile app',
    locationAssignment: 'Location Assignment',
    technicalSpecs: 'Technical Specifications',
    ocppConfig: 'OCPP Configuration',
    statusConfig: 'Status Configuration',
    identification: 'Identification',
    translations: 'Multi-language Translations',
    additionalInfo: 'Additional Information',
    nameHelperText: 'Descriptive name for the charger',
    modelHelperText: 'Charger model (e.g., Tesla Supercharger V3)',
    serialHelperText: 'Manufacturer serial number (optional)',
    siteHelperText: 'Select the site where this charger is located',
    loadGroupHelperText: 'Select the load group for power management',
    selectSiteFirst: 'Please select a premises first to see available load groups',
    physicalLocationHelperText: 'Specific physical location within the site (e.g., Bay 1, North Side)',
    bayNumberHelperText: 'Parking bay printed on the kiosk and receipts (unique within the premises)',
    bayNumberRequiredHelperText: 'Required, because this premises runs a kiosk and the kiosk never offers a charger without a reference number. Printed on the kiosk and receipts (unique within the premises)',
    zoneHelperText: 'Area label shown on the kiosk, e.g. "B2 North"',
    maxPowerHelperText: 'Maximum power output in kW',
    maxCurrentHelperText: 'Maximum current in Amperes (A)',
    maxCurrentHelperTextWithSuggestion: 'Maximum current in Amperes (Suggested: %{suggested})',
    numberPhasesHelperText: 'Set to 1 for single-phase chargers (typically ≤ 7.4 kW), 3 for three-phase. Forced to 1 when the load group is single-phase.',
    numberPhasesForcedSingle: 'Forced to 1 because the selected load group is single-phase.',
    autoCalculated: 'Auto-calculated from power and connector type',
    clickToAutoCalculate: 'Click to auto-calculate current',
    powerDisplayHelperText: 'Power rating for display (e.g., "150kW DC Fast Charging")',
    connectorTypeHelperText: 'Type of charging connector',
    connectorCountHelperText: 'Number of charging connectors',
    maxSimultaneousConnectorsHelperText: 'How many connectors can charge at the same time (1 = only one connector active at a time)',
    ocppVersionHelperText: 'OCPP protocol version',
    ocppTemplateHelperText: 'Select an OCPP configuration template (optional)',
    firmwareHelperText: 'Current firmware version (optional)',
    stationIdHelperText: 'Unique station identifier for OCPP connection (e.g., CS-TESLA-001)',
    authEnabledHelperText: 'Enable HTTP Basic Authentication for OCPP connections',
    authUsernameHelperText: 'Username for OCPP authentication (e.g., CS-TESLA)',
    authPasswordHelperText: 'Password for OCPP authentication',
    statusHelperText: 'Current operational status',
    ocppStatusHelperText: 'OCPP-specific status (optional)',
    errorCodeHelperText: 'Error code if applicable (optional)',
    qrCodeHelperText: 'Leave empty to auto-generate, or enter 4 characters (e.g., A3K9)',
    notesHelperText: 'Additional notes about this charger',
    additionalInfoMessage: 'Additional settings and information can be configured after the charger is created.',
    backToList: 'Back to Chargers',
  },
  edit: {
    title: 'Edit Charger',
    basicInfo: 'Basic Information',
    image: 'Charger Image',
    imageHelperText: 'Upload or change the charger image displayed in the mobile app',
    locationAssignment: 'Location Assignment',
    technicalSpecs: 'Technical Specifications',
    ocppConfig: 'OCPP Configuration',
    statusManagement: 'Status Management',
    chargerReports: 'Charger Reports',
    autoSyncing: 'Auto-syncing from charger',
    manualOverrideActiveMessage: 'Manual override active - OCPP updates will not change status',
    overrideSet: 'Override set',
    statusEffective: 'Effective',
    identification: 'Identification',
    translations: 'Multi-language Translations',
    additionalInfo: 'Additional Information',
    rebootCharger: 'Reboot Charger',
    rebootDescription: 'Remotely reboot this charger via OCPP Reset command. Use this if the charger is unresponsive or needs to restart.',
    chargerOfflineWarning: 'This charger is currently offline. Reboot command cannot be sent.',
    deleteCharger: 'Delete Charger',
    deleteChargerDescription: 'Permanently delete this charger and all associated data',
    cannotDeleteWithActiveSessions: 'Cannot delete charger with active charging sessions',
    connectors: 'Connectors',
    addConnector: 'Add Connector',
    editConnector: 'Edit Connector',
    noConnectors: 'No connectors configured for this charger. Add connectors to enable charging.',
    chargerTypeHelperText: 'Select the charger type category for this connector',
    nameHelperText: 'Descriptive name for the charger',
    modelHelperText: 'Charger model (e.g., Tesla Supercharger V3)',
    serialHelperText: 'Manufacturer serial number',
    siteHelperText: 'Premises where this charger is located',
    loadGroupHelperText: 'Load group for power management',
    selectSiteFirst: 'Please select a premises first to see available load groups',
    physicalLocationHelperText: 'Specific physical location within the premises',
    bayNumberHelperText: 'Parking bay printed on the kiosk and receipts (unique within the premises)',
    bayNumberRequiredHelperText: 'Required, because this premises runs a kiosk and the kiosk never offers a charger without a reference number. Printed on the kiosk and receipts (unique within the premises)',
    zoneHelperText: 'Area label shown on the kiosk, e.g. "B2 North"',
    maxPowerHelperText: 'Maximum power output in kW',
    maxCurrentHelperText: 'Maximum current in Amperes (A)',
    maxCurrentHelperTextWithSuggestion: 'Maximum current in Amperes (Suggested: %{suggested})',
    numberPhasesHelperText: 'Set to 1 for single-phase chargers (typically ≤ 7.4 kW), 3 for three-phase. Forced to 1 when the load group is single-phase.',
    numberPhasesForcedSingle: 'Forced to 1 because the selected load group is single-phase.',
    autoCalculated: 'Auto-calculated from power and connector type',
    clickToAutoCalculate: 'Click to auto-calculate current',
    powerDisplayHelperText: 'Power rating for display',
    connectorTypeHelperText: 'Type of charging connector',
    connectorCountHelperText: 'Number of charging connectors',
    maxSimultaneousConnectorsHelperText: 'How many connectors can charge at the same time (1 = only one connector active at a time)',
    ocppVersionHelperText: 'OCPP protocol version',
    ocppTemplateHelperText: 'Select an OCPP configuration template (optional)',
    firmwareHelperText: 'Current firmware version',
    stationIdHelperText: 'Unique station identifier for OCPP connection',
    authEnabledHelperText: 'Enable/disable HTTP Basic Authentication for OCPP connections',
    authUsernameHelperText: 'Username for OCPP authentication',
    authPasswordHelperText: 'Password for OCPP authentication (leave blank to keep existing)',
    statusHelperText: 'Current operational status',
    statusEffectiveHelperText: 'This is the status shown to users. Changing this will activate manual override.',
    reservationEnabledLabel: 'Enabled for Reservations',
    reservationEnabledHelperText: 'When off, this charger is excluded from the reservation pool regardless of OCPP status. Use for long-term outages or chargers you do not want exposed for reservations.',
    manualOverrideActive: 'Manual Override Active',
    manualOverrideHelperText: "When ON, status won't auto-sync from charger",
    manualOverrideReason: 'Reason for Manual Override',
    manualOverrideReasonPlaceholder: 'e.g., Scheduled maintenance until 5 PM, Network outage',
    manualOverrideReasonHelperText: 'Explain why the manual status was set (visible to admins only)',
    chargerReportedStatusLabel: 'Charger Reported Status (OCPP)',
    chargerReportedStatusHelperText: 'Read-only: Status reported by charger hardware via OCPP',
    errorCodeLabel: 'Error Code',
    errorCodeHelperText: 'OCPP error code (if any)',
    ocppStatusHelperText: 'OCPP-specific status',
    qrCodeHelperText: 'Leave empty to auto-generate, or enter 4 characters (e.g., A3K9)',
    notesHelperText: 'Additional notes about this charger',
    additionalInfoMessage: 'Review charger configuration and make necessary adjustments.',
    backToList: 'Back to Chargers',
    deleteConfirmTitle: 'Delete Charger',
    deleteConfirmWarning: 'Warning: This action cannot be undone.',
    deleteConfirmMessage: 'You are about to delete charger',
    deleteConfirmInfo: 'All associated data will be permanently removed.',
    deleteConfirmPrompt: 'To confirm deletion, please type the charger name',
    deleteConfirmPromptBelow: 'below:',
    deleteConfirmTypePlaceholder: 'Type "%{name}" to confirm',
    nameDoesNotMatch: 'Charger name does not match',
    deleteButton: 'Delete Charger',
  },
  actions: {
    uploadImage: 'Upload Image',
    changeImage: 'Change Image',
    uploading: 'Uploading',
    reboot: 'Reboot Charger',
  },
  show: {
    statusInformation: 'Status Information',
    chargerReportedStatus: 'Charger Reported Status (OCPP)',
    metadata: 'Metadata',
    created: 'Created',
    lastUpdated: 'Last Updated',
    display: 'Display',
    noRebootHistory: 'No reboot history found for this charger.',
  },
  notifications: {
    createSuccess: 'Charger created successfully',
    updateSuccess: 'Charger updated successfully',
    deleteSuccess: 'Charger deleted successfully',
    deleteError: 'Error deleting charger',
    imageUploaded: 'Image uploaded successfully',
    imageRemoved: 'Image removed',
    rebootSuccess: 'Reboot command sent successfully',
  },
  errors: {
    invalidImageType: 'Invalid image type. Please upload a JPG, PNG, or WebP image.',
    uploadFailed: 'Image upload failed. Please try again.',
  },
  multilang: {
    nameHelperText: 'Charger name in this language',
    locationHelperText: 'Physical location description in this language',
    notesHelperText: 'Additional notes in this language',
  },
  validation: {
    bayNumberRequiredForKiosk: 'A reference number is required at a premises that runs a kiosk',
  },
};